import {
  AgentCloudTestConfigChange,
  AgentCloudTestCreationType,
  Period,
  getAgentTestChangeRowId,
  isAgentCloudTestCreateChange,
} from '@elementary/common-ui/types';
import {
  AddTestForm,
  CloudAnomalyTestConfig,
  SeverityFormField,
} from 'shared/types';
import { CloudPlanWizardState } from 'shared/components/AddTestWizard/types';
import { DEFAULT_ANOMALY_TRAINING_PERIOD } from 'shared/components/AddTestWizard/constants';
import { anomalyDirectionOptions, sensitivityOptions } from 'shared/constants';
import {
  Mapper,
  DirtyFields,
  PlanFieldDescriptor,
  changeToForm,
  formToChange,
  listMapper,
  makeSeverityMapper,
  nullableMapper,
  planSeverityToForm,
  setFieldNew,
} from './planFieldMappers';

const PLAN_CREATE_TYPE_TO_WIZARD: Record<
  AgentCloudTestCreationType,
  AddTestForm['testType']
> = {
  freshness: 'cloudFreshnessAnomalies',
  volume: 'cloudVolumeAnomalies',
  data_contract: 'cloudDataContract',
  pipeline_task_performance: 'cloudPipelineTaskPerformance',
};

const TEST_NAME_TO_WIZARD: Record<string, AddTestForm['testType']> = {
  freshness_anomalies: 'cloudFreshnessAnomalies',
  volume_anomalies: 'cloudVolumeAnomalies',
  data_contract: 'cloudDataContract',
  pipeline_task_performance: 'cloudPipelineTaskPerformance',
};

// Nullable Period fields whose empty state is `null` (a radio/switch clears
// them), unlike nullableMapper which uses `undefined` for empty selects.
const nullablePeriodMapper: Mapper = {
  toForm: (planValue) => (planValue as Period | null) ?? null,
  toPlan: (formValue) => (formValue as Period | null) ?? null,
};

const nullableNumberMapper: Mapper = {
  toForm: (planValue) => (planValue as number | null) ?? null,
  toPlan: (formValue) => (formValue as number | null) ?? null,
};

const trainingPeriodMapper: Mapper = {
  toForm: (planValue) =>
    (planValue as Period | null) ?? DEFAULT_ANOMALY_TRAINING_PERIOD,
  toPlan: (formValue) => (formValue as Period | null) ?? null,
};

// The plan carries only the fields the agent proposed to change, not the test's
// full current config. For anomaly knobs the wizard always renders, fall back to
// the wizard's own create defaults so the form never opens blank (an impossible
// state for a real test). These defaults aren't marked dirty, so leaving them
// untouched won't write them back to the plan.
const DEFAULT_SENSITIVITY = sensitivityOptions[1].value;
const sensitivityMapper: Mapper = {
  toForm: (planValue) => (planValue as string | null) ?? DEFAULT_SENSITIVITY,
  toPlan: (formValue) => (formValue as string | null) ?? null,
};

const DEFAULT_ANOMALY_DIRECTION = anomalyDirectionOptions[0].value;
const anomalyDirectionMapper: Mapper = {
  toForm: (planValue) =>
    (planValue as string | null) ?? DEFAULT_ANOMALY_DIRECTION,
  toPlan: (formValue) => (formValue as string | null) ?? null,
};

const DEFAULT_DETECTION_PERIOD: Period = { count: 2, period: 'day' };
const detectionPeriodMapper: Mapper = {
  toForm: (planValue) =>
    (planValue as Period | null) ?? DEFAULT_DETECTION_PERIOD,
  toPlan: (formValue) => (formValue as Period | null) ?? null,
};

const baselineColumnsMapper: Mapper = {
  toForm: (planValue) => (Array.isArray(planValue) ? planValue : []),
  toPlan: (formValue) => (Array.isArray(formValue) ? formValue : []),
};

const CLOUD_DESCRIPTORS: PlanFieldDescriptor[] = [
  {
    planKey: 'sensitivity',
    formPath: 'anomalyConfig.sensitivity',
    mapper: sensitivityMapper,
  },
  {
    planKey: 'threshold',
    formPath: 'anomalyConfig.fixedThreshold',
    mapper: nullablePeriodMapper,
  },
  {
    planKey: 'anomalyDirection',
    formPath: 'anomalyConfig.anomalyDirection',
    mapper: anomalyDirectionMapper,
  },
  {
    planKey: 'detectionPeriod',
    formPath: 'anomalyConfig.detectionPeriod',
    mapper: detectionPeriodMapper,
  },
  {
    planKey: 'spikeThreshold',
    formPath: 'anomalyConfig.spikeThreshold',
    mapper: nullableNumberMapper,
  },
  {
    planKey: 'dropThreshold',
    formPath: 'anomalyConfig.dropThreshold',
    mapper: nullableNumberMapper,
  },
  {
    planKey: 'trainingPeriod',
    formPath: 'anomalyConfig.trainingPeriod',
    mapper: trainingPeriodMapper,
  },
  {
    planKey: 'failureThresholdGrace',
    formPath: 'anomalyConfig.failureThresholdGrace',
    mapper: nullablePeriodMapper,
  },
  {
    planKey: 'severity',
    formPath: 'severity',
    mapper: makeSeverityMapper('upper'),
  },
  { planKey: 'owners', formPath: 'owners', mapper: listMapper },
  { planKey: 'tags', formPath: 'tags', mapper: listMapper },
  {
    planKey: 'baselineMode',
    formPath: 'baselineMode',
    mapper: nullableMapper,
  },
  {
    planKey: 'baseline',
    formPath: 'baselineColumns',
    mapper: baselineColumnsMapper,
  },
  {
    planKey: 'enforcementLevel',
    formPath: 'enforcementLevel',
    mapper: nullableMapper,
  },
];

export const resolveCloudWizardTestType = (params: {
  change: AgentCloudTestConfigChange;
  testName?: string;
}): AddTestForm['testType'] | null => {
  if (isAgentCloudTestCreateChange(params.change)) {
    return PLAN_CREATE_TYPE_TO_WIZARD[params.change.testType] ?? null;
  }
  if (!params.testName) return null;
  return TEST_NAME_TO_WIZARD[params.testName] ?? null;
};

// `useTestConfiguration` rewrites a cloud test's severity to the
// CloudTestsSeverity numbers (WARNING = 1, ERROR = 10) before the config reaches
// us, while the plan carries the enum names — accept either shape.
const CLOUD_SEVERITY_NUMBER_TO_NAME: Record<number, string> = {
  1: 'WARNING',
  10: 'ERROR',
};

const cloudConfigSeverityToForm = (
  severity: number | string,
): SeverityFormField =>
  planSeverityToForm(
    typeof severity === 'number'
      ? CLOUD_SEVERITY_NUMBER_TO_NAME[severity]
      : severity,
  ) ?? { type: 'warn' };

const cloudTestConfigToFormValues = (
  config: CloudAnomalyTestConfig,
): Partial<AddTestForm> => {
  const anomalyConfig =
    config.type === 'anomaly_detection'
      ? {
          sensitivity: config.anomaly_config.sensitivity,
          anomalyDirection: config.anomaly_config.anomaly_direction,
          detectionPeriod: config.anomaly_config.detection_period,
          trainingPeriod: config.anomaly_config.training_period ?? null,
          fixedThreshold: null,
          failureThresholdGrace: null,
          spikeThreshold: config.anomaly_config.spike_threshold ?? null,
          dropThreshold: config.anomaly_config.drop_threshold ?? null,
        }
      : {
          sensitivity: config.anomaly_config.sensitivity,
          trainingPeriod: config.anomaly_config.training_period ?? null,
          fixedThreshold: config.anomaly_config.fixed_threshold ?? null,
          failureThresholdGrace:
            config.anomaly_config.failure_threshold_grace ?? null,
          spikeThreshold: null,
          dropThreshold: null,
        };

  return {
    anomalyConfig,
    severity: cloudConfigSeverityToForm(config.severity),
    owners: config.owners ?? [],
    tags: config.tags ?? [],
  };
};

export const buildCloudPlanWizardState = (params: {
  change: AgentCloudTestConfigChange;
  operation: 'create' | 'update';
  assetId: string;
  testName?: string;
  currentConfig?: CloudAnomalyTestConfig;
}): CloudPlanWizardState | null => {
  const wizardTestType = resolveCloudWizardTestType({
    change: params.change,
    testName: params.testName,
  });
  if (!wizardTestType) return null;

  const hasCurrentConfig = params.currentConfig != null;
  const base: Partial<AddTestForm> = {
    resourceType: 'tables',
    resources: [params.assetId],
    testType: wizardTestType,
    anomalyConfig: { fixedThreshold: null, failureThresholdGrace: null },
    ...(params.currentConfig
      ? cloudTestConfigToFormValues(params.currentConfig)
      : {}),
  };

  // Pipeline plans use `fixedThreshold`; freshness uses `threshold`. Both
  // bind to the same wizard field, so alias the pipeline key for seeding.
  const changeForForm =
    params.change.fixedThreshold !== undefined &&
    params.change.threshold === undefined
      ? { ...params.change, threshold: params.change.fixedThreshold }
      : params.change;

  const initialValues = changeToForm(changeForForm, CLOUD_DESCRIPTORS, base, {
    overlayOnly: hasCurrentConfig,
  });

  if (wizardTestType === 'cloudDataContract') {
    initialValues.baselineMode = initialValues.baselineMode ?? 'complete';
    initialValues.enforcementLevel =
      initialValues.enforcementLevel ?? 'downstream';
    initialValues.baselineColumns = initialValues.baselineColumns ?? [];
  }

  const mergedAnomalyConfig = initialValues.anomalyConfig;
  const hasSpike = mergedAnomalyConfig?.spikeThreshold != null;
  const hasDrop = mergedAnomalyConfig?.dropThreshold != null;
  const hasPercentageThresholds = hasSpike || hasDrop;

  initialValues.volumeDetectionMode = hasPercentageThresholds
    ? 'percentage_thresholds'
    : 'anomaly_detection';

  // In percentage mode the direction decides which threshold inputs render (and
  // are required). Narrow it to the thresholds that actually hold a value —
  // whether from the plan or the test's real config — so the wizard never opens
  // with an empty, required threshold input. Not marked dirty, so a narrowing
  // the user leaves alone is never written back to the plan.
  if (hasPercentageThresholds && mergedAnomalyConfig) {
    mergedAnomalyConfig.anomalyDirection =
      hasSpike && hasDrop ? 'BOTH' : hasSpike ? 'SPIKE' : 'DROP';
  }

  return {
    planSource: 'cloud-plan',
    planRowId: getAgentTestChangeRowId(params.change),
    operation: params.operation,
    wizardResourceType: 'tables',
    initialValues,
  };
};

export const applyFormToCloudChange = (
  change: AgentCloudTestConfigChange,
  form: AddTestForm,
  dirtyFields: DirtyFields,
): AgentCloudTestConfigChange => {
  const next = formToChange(change, form, dirtyFields, CLOUD_DESCRIPTORS);
  // Percentage thresholds and ML detection are mutually exclusive. In ML mode
  // the threshold inputs are hidden but their values persist (so toggling modes
  // doesn't wipe them) — strip them from the change so they don't leak into the
  // plan while the user has chosen anomaly detection.
  if (form.volumeDetectionMode === 'anomaly_detection') {
    delete next.spikeThreshold;
    delete next.dropThreshold;
  }
  if (
    form.testType === 'cloudPipelineTaskPerformance' &&
    next.threshold !== undefined
  ) {
    next.fixedThreshold = setFieldNew(
      change.fixedThreshold,
      next.threshold.new,
    );
    delete next.threshold;
  }
  return next;
};
